/**
 * Marks — the Wall of Marks, plus the Wardrobe it feeds.
 *
 * The owner's note: "Marks = achievements, I can get on board with that but needs icons and
 * animations." So every Mark now has its own drawing (`markIcon`, keyed by id in icons.tsx, with
 * a per-counter category fallback so a Mark shipped by the server that this build has never heard
 * of still gets an honest glyph), a tier rail, and a state that is legible at a glance:
 *
 *   locked   — dim, the rail unlit, the bar showing the road to bronze.
 *   unlocked — the rail takes the tier's colour and the drawing comes up to full.
 *   hidden   — a rumour and a question mark until the first tier lands, because spoiling
 *              "Bad Landing" spoils the joke.
 *
 * Tiers re-colour into the Requiem palette rather than the metals they are named for — bronze is
 * lilac-dim, amber is mint, Hymn is moon over a wine rail. Nothing here is orange or gold.
 *
 * The one animation with state behind it is the reveal: a Mark whose tier is higher than the last
 * tier this browser saw stamps itself in, once, and the new tier is written to localStorage so the
 * second visit is calm. Titles and lantern colours are Mark rewards, so the Wardrobe lives here
 * rather than in Settings.
 */

import type { VNode } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { MARKS, MarkTier, markTier, type MarkDef } from '@stillsun/shared';
import {
  fetchMarks,
  postWardrobe,
  type MarkRecord,
  type ProgressResponse,
} from '../../net/progress-api.js';
import { progressStore } from '../../net/progress-store.js';
import { Icon, markIcon } from '../icons.js';
import {
  LEDGER_SILENT,
  Loading,
  Notice,
  Section,
  errText,
  useLoader,
  useProgress,
} from './common.js';
import './marks.css';

export interface MarksProps {
  nonce: number;
  token: string;
}

const TIER_CLASS = ['locked', 'bronze', 'amber', 'hymn'] as const;
const TIER_NAME = ['Unearned', 'Struck', 'Lit', 'Hymn'] as const;

const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`;

/**
 * Lantern colours a player can pick, whether or not a Mark has granted them yet.
 *
 * Read out of `MARKS` rather than hardcoded, so the picker is exactly the set the game actually
 * grants and cannot drift from the contract, plus two neutral Requiem tones for anyone who wants
 * a lantern that says nothing. The two pre-Requiem orange rewards were re-toned at the source in
 * M6 (`m_dimming` → epic purple, `m_verger` → Hymn magenta); orange is rarity-only now.
 */
const LANTERN_SWATCHES: number[] = [
  ...new Set([
    ...MARKS.map((m) => m.reward?.lanternColor).filter((c): c is number => typeof c === 'number'),
    0x9fb8d6,
    0x7fcf8a,
  ]),
];

function nextThreshold(def: MarkDef, tier: number): number | null {
  return tier >= 3 ? null : (def.thresholds[tier] ?? null);
}

// ------------------------------------------------------- what this browser has seen

const SEEN_KEY = 'stillsun.marks.seen';

/** Tier per Mark id as of this browser's last visit. A private window simply reveals again. */
function readSeen(): Record<string, number> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writeSeen(tiers: Record<string, number>): void {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify(tiers));
  } catch {
    /* private mode: the reveal simply plays again next time, which is harmless */
  }
}

export function Marks(props: MarksProps): VNode {
  const { token } = props;
  const progress = useProgress(token, props.nonce);
  const mirror = useLoader<MarkRecord[]>(
    () => fetchMarks(token),
    `marks:${String(props.nonce)}:${token}`,
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  // Read once per mount: the set the reveal animation is measured against must not move while
  // the wall is on screen, or a card would un-reveal itself under the player's eyes.
  const [seen] = useState<Record<string, number>>(readSeen);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());

  const data = progress.data;

  /** Highest tier from either source: `/api/progress` and `/api/marks` are mirrors. */
  const earned = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of data?.marks ?? []) map.set(m.id, m.tier);
    for (const m of mirror.data ?? []) map.set(m.id, Math.max(map.get(m.id) ?? 0, m.tier));
    return map;
  }, [data, mirror.data]);

  // Anything above the tier this browser last wrote down is new. The class is applied for one
  // animation, then the store is updated so the next visit is quiet.
  useEffect(() => {
    if (data === null) return;
    const fresh = new Set<string>();
    const next: Record<string, number> = { ...seen };
    for (const def of MARKS) {
      const tier = Math.max(
        earned.get(def.id) ?? 0,
        markTier(def, data.counters[def.counter] ?? 0),
      );
      if (tier > (seen[def.id] ?? 0)) fresh.add(def.id);
      next[def.id] = tier;
    }
    setRevealed(fresh);
    writeSeen(next);
  }, [data, earned, seen]);

  if (progress.loading && !progress.loaded) {
    return (
      <div class="hub-pane">
        <Loading />
      </div>
    );
  }
  if (data === null) {
    return (
      <div class="hub-pane">
        <Notice>{LEDGER_SILENT}</Notice>
      </div>
    );
  }

  const wear = (change: { title?: string; lanternColor?: number }): void => {
    if (busy) return;
    setBusy(true);
    setNote('');
    // `exactOptionalPropertyTypes`: an absent title is an absent *key*, never `undefined`.
    const before: Partial<ProgressResponse> = {
      ...(data.activeTitle === undefined ? {} : { activeTitle: data.activeTitle }),
      ...(data.lanternColor === undefined ? {} : { lanternColor: data.lanternColor }),
    };
    progressStore.patch(change);
    void (async () => {
      try {
        await postWardrobe(token, change);
        await progressStore.load(token, true);
      } catch (err) {
        progressStore.patch(before);
        setNote(errText(err, 'The Compact would not write that down.'));
      } finally {
        setBusy(false);
      }
    })();
  };

  // Counted the same way the cards are drawn: the server's recorded tier, or the one the live
  // counter has already reached, whichever is higher. Counting only the recorded tier would say
  // "1 of 54" under a wall showing eight lit Marks.
  const shownTier = (def: MarkDef): number =>
    Math.min(3, Math.max(earned.get(def.id) ?? 0, markTier(def, data.counters[def.counter] ?? 0)));
  const totalTiers = MARKS.reduce((sum, m) => sum + shownTier(m), 0);
  const maxTiers = MARKS.length * 3;

  return (
    <div class="hub-pane mk">
      {note.length > 0 ? <p class="hub-error">{note}</p> : null}

      <Section title="Wardrobe" hint="Titles and lantern colours come from Marks">
        <div class="hub-wardrobe">
          <div class="hub-wardrobe-row">
            <span class="hub-label">Title</span>
            <div class="hub-chips">
              <button
                type="button"
                class={`hub-chip${(data.activeTitle ?? '') === '' ? ' on' : ''}`}
                disabled={busy}
                onClick={() => {
                  wear({ title: '' });
                }}
              >
                None
              </button>
              {data.titles.map((t) => (
                <button
                  key={t}
                  type="button"
                  class={`hub-chip${data.activeTitle === t ? ' on' : ''}`}
                  disabled={busy}
                  onClick={() => {
                    wear({ title: t });
                  }}
                >
                  {t}
                </button>
              ))}
              {data.titles.length === 0 ? (
                <span class="hub-hint">No titles yet. Marks grant them at Hymn tier.</span>
              ) : null}
            </div>
          </div>

          <div class="hub-wardrobe-row">
            <span class="hub-label">Lantern</span>
            <div class="hub-swatches">
              {LANTERN_SWATCHES.map((c) => (
                <button
                  key={String(c)}
                  type="button"
                  class={`hub-swatch${data.lanternColor === c ? ' on' : ''}`}
                  style={{ background: hex(c) }}
                  aria-label={`Lantern ${hex(c)}`}
                  disabled={busy}
                  onClick={() => {
                    wear({ lanternColor: c });
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      <Section title="Marks" hint={`${String(totalTiers)} of ${String(maxTiers)} tiers earned`}>
        <div class="mk-wall">
          {MARKS.map((def) => {
            const value = data.counters[def.counter] ?? 0;
            // Only trust the local counter when it is not behind the server's own tier.
            const tier = shownTier(def);
            // A hidden Mark you have not started is a rumour.
            const secret = def.hidden === true && tier === 0;
            const goal = nextThreshold(def, tier);
            const prev = tier === 0 ? 0 : (def.thresholds[tier - 1] ?? 0);
            const pct =
              goal !== null && goal > prev
                ? Math.max(0, Math.min(100, ((value - prev) / (goal - prev)) * 100))
                : 100;
            const cls = [
              'mk-card',
              TIER_CLASS[tier] ?? 'locked',
              secret ? 'secret' : '',
              revealed.has(def.id) && tier > 0 ? 'reveal' : '',
            ]
              .filter((s) => s.length > 0)
              .join(' ');
            return (
              <article key={def.id} class={cls}>
                <span class="mk-rail" aria-hidden="true" />
                <div class="mk-glyph" aria-hidden="true">
                  {secret ? (
                    <span class="mk-secret">?</span>
                  ) : (
                    <Icon name={markIcon(def.id, def.counter)} size={30} />
                  )}
                  <span class="mk-pips">
                    {[1, 2, 3].map((t) => (
                      <i key={String(t)} class={tier >= t ? 'on' : ''} />
                    ))}
                  </span>
                </div>
                <div class="mk-body">
                  <h3 class="mk-name">{secret ? 'Hidden Mark' : def.name}</h3>
                  <p class="mk-flavour">
                    {secret ? 'Something you have not done yet.' : def.flavour}
                  </p>
                  {secret ? null : (
                    <>
                      <div class="mk-bar">
                        <div class="mk-bar-fill" style={{ width: `${String(pct)}%` }} />
                      </div>
                      <div class="mk-foot">
                        <span class="mk-tier">{TIER_NAME[tier] ?? 'Unearned'}</span>
                        <span class="mk-count">
                          {goal === null
                            ? 'Complete'
                            : `${String(Math.min(value, goal))} / ${String(goal)}`}
                        </span>
                      </div>
                      {def.reward?.title !== undefined && tier >= MarkTier.Hymn ? (
                        <span class="mk-reward">{`Title: ${def.reward.title}`}</span>
                      ) : null}
                    </>
                  )}
                </div>
                <span class="mk-stamp" aria-hidden="true" />
              </article>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
